import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import ApiService from '../../common/services/ApiService';
import { checkAuth, useLogIn } from '../redux/logIn';
import { isEmpty, isObject } from 'lodash';
import SessionStorage from '../../common/services/SessionStorage';
import {
  TWO_FACTOR_EXPIRED_ERROR_CODE,
  TWO_FACTOR_UNVERIFIED_ERROR_CODE,
} from '../constants/TwoFactor';
import Swal from 'sweetalert2';

const USER_KEY = 'my-user';

export const getUser = () => {
  return SessionStorage.get(USER_KEY);
};

export const setUser = (userData) => {
  SessionStorage.remove(USER_KEY);
  return SessionStorage.set(USER_KEY, userData);
};

export const logoutWithoutForward = () => {
  SessionStorage.remove(USER_KEY);
  ApiService.logout();
};

export const logout = () => {
  SessionStorage.remove(USER_KEY);
  ApiService.logout().finally(() => {
    window.location.href = '/login';
  });
};

export const useAuth = ({ middleware, redirectIfAuthenticated } = {}) => {
  const history = useHistory();
  const { logIn, logInPending, logInError, dismissLogInError } = useLogIn();
  const currentUser = getUser();
  const [authenticated, setAuthenticated] = useState(!!currentUser);
  const [user, setUserState] = useState(currentUser || {});
  const [loading, setLoading] = useState(false);

  const safeRedirect = useCallback((targetPath) => {
    if (history.location.pathname !== targetPath) {
      history.replace(targetPath);
    }
  }, [history]);

  const login = async (data) => {
    setLoading(true);
    logIn(data)
      .then((userData) => {
        if (isObject(userData) && !isEmpty(userData)) {
          SessionStorage.set(USER_KEY, userData);
          setUserState(userData);
          setAuthenticated(true);
        } else {
          handleLogout();
        }
      })
      .catch((error) => {
        if (error && error.status) {
          switch (error.status) {
            case 409:
              safeRedirect('/account/activate');
              break;
            case 422:
              throw error;
            case 401:
            default:
              safeRedirect('/login');
          }
        }
      })
      .finally(() => setLoading(false));
  };

  const handleLogout = useCallback(() => {
    SessionStorage.remove(USER_KEY);
    setAuthenticated(false);
    ApiService.logout().finally(() => {
      safeRedirect('/login');
    });
  }, [safeRedirect]);

  useEffect(() => {
    if (logInPending) return; // Don't run logic while logging in

    if (!isObject(user)) return;

    if (isEmpty(user)) {
      if (history.location.pathname !== '/login') {
        safeRedirect('/login');
      }
      return;
    }

    checkAuth()
      .then(() => {
        if (middleware === 'guest' && redirectIfAuthenticated && user) {
          const isAdmin = user.role === 'Admin';
          const isProvider = user.role === 'Provider';
          const goDashboard = redirectIfAuthenticated.includes('dashboard');
          const goReferral = redirectIfAuthenticated.includes('referral');
          const noReferralPermissions = user.referral_permissions === null;
          const hasPermissions = user.referral_permissions?.some(
            e =>
              ['ACCEPT_REFERRAL', 'SCHEDULE_REFERRAL', 'SEND_REFERRAL'].includes(e.key) &&
              e.value
          );

          // Admin: referral section
          if (isAdmin && goReferral) {
            if (
              !redirectIfAuthenticated.includes('/manage/referrals') &&
              !redirectIfAuthenticated.includes('/manage/referral-networks')
            ) {
              safeRedirect('/manage/clinics');
              return;
            }
          }

          // Provider section
          if (isProvider && goDashboard) {
            safeRedirect('/provider');
            return;
          }

          // Non-admin: no referral permissions
          if (!isAdmin && noReferralPermissions && goDashboard) {
            Swal.fire({
              icon: 'error',
              text: 'You have no referral permissions. Please contact the system administrator.',
              showConfirmButton: true,
            }).then(handleLogout);
            return;
          }

          // Non-admin: has permissions
          if (!isAdmin && hasPermissions && goDashboard) {
            safeRedirect('/referrals/active');
            return;
          }

          // Non-admin: dashboard but no permissions
          if (!isAdmin && goDashboard) {
            safeRedirect('/referrals/declined');
            return;
          }

          // Default
          safeRedirect(redirectIfAuthenticated);
        }
      })
      .catch((err) => {
        if (
          err?.data?.code === TWO_FACTOR_EXPIRED_ERROR_CODE ||
          err?.data?.code === TWO_FACTOR_UNVERIFIED_ERROR_CODE
        ) {
          safeRedirect('/auth/2fa/verify');
        } else if (err?.status === 401 || err?.status === 405) {
          handleLogout();
        }
      });
  }, [user, safeRedirect, handleLogout, history, redirectIfAuthenticated, middleware, logInPending]);

  return {
    user,
    isLoadingUser: loading,
    menuItems: user && Array.isArray(user.menu_items)
      ? { items: user.menu_items }
      : { items: [] },
    login,
    logInPending,
    logInError,
    dismissLogInError,
    authenticated,
    logout: handleLogout,
    loggedInAt: user?.loggedInAt,
  };
};
